import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, firstValueFrom, Observable } from "rxjs";
import { ToastrService } from "ngx-toastr";
import { APIConfigMapping } from "../vl-http/api-mapping-config.cnst";
import { HTTPAPIConfig } from "../vl-http/http-api.config";
import { VlDataService } from "../vl-data/vl-data.service";
import { VlHttpService } from "../vl-http/vl-http.service";
import { USERDEFINEDAPIVARIABLE,USERDEFINEDAPI } from "./model/apiConfig.model";
import { ACCESS_TOKEN_SYNONYMS } from "../../constants/proj.cnst";

@Injectable({
  providedIn: "root",
})
export class ApiconfigService {
  constructor(
    private toasterService: ToastrService,
    private http: HttpClient,
    private httpService: VlHttpService,
    private dataService: VlDataService
  ) {}

  private selectedApiSubject = new BehaviorSubject<any>({});

  selectedApi$: Observable<any> = this.selectedApiSubject.asObservable();

  setSelectedApi(api: any) {
    this.selectedApiSubject.next(api);
  }

  private userApis = new BehaviorSubject<any>({});

  userApis$: Observable<any> = this.userApis.asObservable();

  setUserApis(apis: any) {
    this.userApis.next(apis);
  }

  getUserApis() {
    return this.userApis.value;
  }

  getUserApi(id: string) {
    const apiConfig: HTTPAPIConfig = this.dataService.deepClone(
      APIConfigMapping["GET_API"]
    );
    apiConfig.postData = {
      api_id: id,
    };

    this.httpService.placeAPICall(apiConfig).subscribe({
      next: (data: any) => {},
      error: (err: any) => {},
      complete: () => {},
    });
  }

  addUserApi(apiData: any, authApi: boolean) {
    const apiConfig: HTTPAPIConfig = this.dataService.deepClone(
      APIConfigMapping["CREATE_CUSTOM_API"]
    );
    let headers = apiData.apiheaders ? this.convertArrayToObject(apiData.apiheaders) : null;
    if (!authApi) {
      apiConfig.postData = {
        apiName: apiData.name,
        apiURL: apiData.url,
        method: apiData.method,
        requestBody: apiData.body,
        headers: headers,
        pathVariables: {},
        queryParams: apiData.queryParameters,
        authenticatedApiId: !authApi ? apiData.authentication : null,
        variables: apiData.variables,
        isAuthenticatedApi: authApi,
        authKey:apiData.authKey,
        extraHeaders:apiData.extraHeader
      };
    } else {
      let headers = this.convertArrayToObject(apiData.headers);
      apiConfig.postData = {
        apiName: apiData.name,
        apiURL: apiData.url,
        method: "POST",
        requestBody: apiData.body,
        headers: headers,
        isAuthenticatedApi: authApi,
        authType: apiData.authType,
      };
    }

    return this.httpService.placeAPICall(apiConfig)
  }

  deleteUserApi(id: string) {
    const apiConfig: HTTPAPIConfig = this.dataService.deepClone(
      APIConfigMapping["DELETE_API"]
    );
    apiConfig.postData = {
      apiId: id,
    };

    this.httpService.placeAPICall(apiConfig).subscribe({
      next: (data: any) => {},
      error: (err: any) => {},
      complete: () => {
        this.getAllApisResquest();
      },
    });
  }

  updateUserApi(apiData: any, authApi: boolean, apiId: string) {
    const apiConfig: HTTPAPIConfig = this.dataService.deepClone(
      APIConfigMapping["UPDATE_CUSTOM_API"]
    );
    if (!authApi) {
      let headers = this.convertArrayToObject(apiData.apiheaders);
      apiConfig.postData = {
        apiId: apiId,
        apiName: apiData.name,
        apiURL: apiData.url,
        method: apiData.method,
        requestBody: apiData.body,
        headers: headers,
        pathVariables: {},
        queryParams: apiData.queryParameters,
        authenticatedApiId: !authApi ? apiData.authentication : null,
        variables: apiData.variables,
        isAuthenticatedApi: authApi,
      };
    } else {
      let bodyData = {};
      let headers = this.convertArrayToObject(apiData.headers);
      apiData.authType == "user"
        ? (bodyData = apiData.authApiuserCreds)
        : (bodyData = apiData.authApiEmailCreds);
      apiConfig.postData = {
        apiId: apiId,
        apiName: apiData.name,
        apiURL: apiData.url,
        method: "POST",
        requestBody: apiData.body,
        headers: headers,
        isAuthenticatedApi: authApi,
        authType: apiData.authType,
      };
    }
    return this.httpService.placeAPICall(apiConfig);
  }

  async getAllApisResquest() {
    const apiConfig: HTTPAPIConfig = this.dataService.deepClone(
      APIConfigMapping["GET_ALL_APIS"]
    );
    apiConfig.postData = {};

    try {
      const data = await firstValueFrom(this.httpService.placeAPICall(apiConfig));
      this.setUserApis(data.result);
    } catch (error:any) {
      if(error?.status == 403) {
        this.toasterService.error('You do not have permission to access this resource.');
      }
      console.error("Failed to fetch all APIs:", error);
      throw error;
    }
  }

  async getApiOrEntityDefinitionBasedOnForm(type: string, form: string) {
    try {
      let formValue = (this[form as keyof typeof this] as any)?.value;
      if (!formValue) {
        throw new Error("Invalid form");
      }
      if (type === "api") {
        const headers = new HttpHeaders();
        if (formValue.headerParamsRequired) {
          formValue.headers?.forEach(
            (header: { key: string; value: string }) => {
              headers.set(header.key, header.value);
            }
          );
        }
        const postData: any = {};
        if (formValue.apiPostData) {
          formValue.apiPostData.forEach(
            (param: { key: string; value: any }) => {
              postData[param.key] = param.value;
            }
          );
        }
        this.makeDataSourceApiRequest(
          formValue.apiUrl,
          formValue.httpMethod,
          postData,
          headers
        );
      }
    } catch (error: any) {
      this.handleApiError(error);
    }
  }

  getValidTokenFromApi(authResponce: any)  {
    // Common base token field names (in lowercase)
    const tokenBaseNames = ACCESS_TOKEN_SYNONYMS
    let token = null;

    // Convert all object keys to lowercase for comparison
    const authResponseLowerCase = Object.keys(authResponce).reduce((acc: any, key) => {
      acc[key.toLowerCase()] = authResponce[key];
      return acc;
    }, {});

    for (const field of tokenBaseNames) {
      if (authResponseLowerCase[field]) {
        token = authResponseLowerCase[field];
        break;
      }
    }

    if (!token) {
      this.toasterService.error(`No valid authentication token found in response. Looking for fields: ${tokenBaseNames.join(", ")}`);
      return;
    }
    return token;
  }

  async makeApiRequestWithDefaultValues(apiId:string) : Promise<{status:boolean,response:any}> {
    try {
      let selectedApi: USERDEFINEDAPI = this.userApis.value.apis.find(
        (api: any) => api.apiId == apiId
      );
      if (!selectedApi) throw new Error("API not found");
  
      let apiURL = this.replaceVariablesWithDefaultValue(selectedApi.apiURL, selectedApi.variables);
      let requestBody = this.replaceVariablesWithDefaultValue(selectedApi.requestBody, selectedApi.variables);
  
      if (selectedApi.authenticatedApiId) {
        let authApi = this.userApis.value.authTypeApis.find((api: any) => api.apiId === selectedApi.authenticatedApiId);
        if (!authApi) throw new Error("Auth API not found");
  
        let authResponse: any = await this.makeDataSourceAuthApiRequest(authApi.apiURL, authApi.method, authApi.requestBody, authApi.headers);
        let token = this.getValidTokenFromApi(authResponse);
          if(token){
            selectedApi.headers['Content-Type'] = "application/json; charset=utf-8";
            selectedApi.headers['Authorization'] = `Bearer ${token}`;
          }
      }
  
      let apiResponse = await this.makeDataSourceApiRequest(apiURL, selectedApi.method, requestBody, selectedApi.headers);
      return {response:apiResponse,status:true};
    } catch (error: any) {
      return {response:error.message,status:false};;
    }
  }

  async makeDataSourceApiRequest(
    apiUrl: string,
    method: string,
    requestData: any,
    headers: any,
    queryParameters?: any,
    varaibles?: any
  ): Promise<any> {
    try {
      const apiConfig: HTTPAPIConfig = this.dataService.deepClone(
        APIConfigMapping["GET_CUSTOM_API_RESPONSE"]
      );
      apiConfig.postData = {
        api_payload: requestData && typeof requestData === 'string'
          ? JSON.parse(requestData)
          : requestData,
          api_url: apiUrl,
          api_method: method,
          api_headers: headers,
          api_query_params: queryParameters,
      };
      
      const response = await firstValueFrom(this.httpService.placeAPICall(apiConfig));
      return response?.response
      } catch (error: any) {
      if (error?.status === 403) {
        this.toasterService.error('You do not have permission to access this resource.');
      } else if (error?.status === 0) {
        this.toasterService.error('Network error or CORS issue occurred.');
      } else {
        this.toasterService.error('An error occurred while making the API request.');
      }
      throw error;
    }
  }

  async makeDataSourceAuthApiRequest(
    apiUrl: string,
    method: string,
    requestData: any,
    headers: any
  ): Promise<any> {
    try {
      const apiConfig: HTTPAPIConfig = this.dataService.deepClone(
        APIConfigMapping["GET_CUSTOM_API_RESPONSE"]
      );
      apiConfig.postData = {
          api_payload: requestData,
          api_url: apiUrl,
          api_method: method,
          api_headers: headers,
      };
      
      const response = await firstValueFrom(this.httpService.placeAPICall(apiConfig));
      return response?.response
      } catch (error: any) {
      if (error?.status === 403) {
        this.toasterService.error('You do not have permission to access this resource.');
      } else if (error?.status === 0) {
        this.toasterService.error('Network error or CORS issue occurred.');
      } else {
        this.toasterService.error( 'An error occurred while making the API request.');
      }
      throw error;
    }
  }

  handleResponse(response: any) {
    let sourceData: any[] = [];
    if (Array.isArray(response) && response.length > 0) {
      sourceData = response;
    } else if (
      typeof response === "object" &&
      Array.isArray(response.data) &&
      response.data.length > 0
    ) {
      sourceData = response.data;
    } else {
      this.toasterService.warning("The Api Does not return list of values");
    }

    return sourceData;
  }

  handleApiError(error: any): void {
    if (error.status === 0) {
      this.toasterService.error(
        "CORS error: Unable to make the API call due to cross-origin restrictions."
      );
    } else {
      this.toasterService.error(error.message);
    }
  }

  mergeObjects(obj1: any, obj2: any): any {
    if (typeof obj1 !== "object" || typeof obj2 !== "object") {
      throw new Error("Both inputs must be objects");
    }
    const mergedObj = { ...obj1 };

    for (const key in obj2) {
      if (obj2.hasOwnProperty(key)) {
        if (typeof obj2[key] === "object" && !Array.isArray(obj2[key])) {
          mergedObj[key] = this.mergeObjects(mergedObj[key], obj2[key]);
        } else {
          mergedObj[key] = obj2[key];
        }
      }
    }
    return mergedObj;
  }

  convertArrayToObject(data: { key: string; value: string }[]): {
    [key: string]: string;
  } {
    const result: { [key: string]: string } = {};

    data.forEach((item) => {
      result[item.key] = item.value;
    });

    return result;
  }

  convertObjectToArray(obj: {
    [key: string]: string;
  }): { key: string; value: string }[] {
    const result: { key: string; value: string }[] = [];

    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        result.push({ key, value: obj[key] });
      }
    }

    return result;
  }

  appendQueryParameters(url: string, queryParams: any): string {
    if (!queryParams || Object.keys(queryParams).length === 0) {
      return url;
    }
    const queryString = queryParams.map((param:{key:string,value:any}) => encodeURIComponent(param.key) + "=" + encodeURIComponent(param.value))
    .join("&");

    const separator = url.includes("?") ? "&" : "?";
    return url + separator + queryString;
  }

  replacePlaceholders(
    apiItem: string,
    keyValueList: { key: string; value: any,defaultValue:any,checked?:boolean }[]
  ): string {
    // Iterate over each key-value pair
    keyValueList.forEach((item) => {
      if(item.checked){
      const placeholder = "${" + item.key + "}";
      // Replace all occurrences of the placeholder with the corresponding value
      apiItem = apiItem.split(placeholder).join(item.value);
      }else{
        const placeholder = "${" + item.key + "}";
      // Replace all occurrences of the placeholder with the corresponding value
      apiItem = apiItem.split(placeholder).join(item.defaultValue);
      }
    });
    return apiItem;
  }

  replaceHeaders(apiItem: string,
    keyValueList: { key: string; value: any,defaultValue:any,checked?:boolean }[]){
      
       keyValueList.forEach((item) => {
      if(item.checked){
      const placeholder = "${" + item.key + "}";
      // Replace all occurrences of the placeholder with the corresponding value
      apiItem = apiItem.split(placeholder).join(item.value);
      }else{
        const placeholder = "${" + item.key + "}";
      // Replace all occurrences of the placeholder with the corresponding value
      apiItem = apiItem.split(placeholder).join(item.defaultValue);
      }
    });
    return apiItem;
  }

  replaceVariablesWithDefaultValue(
    apiItem: string,
    keyValueList: USERDEFINEDAPIVARIABLE[]
  ): string {
    // Iterate over each key-value pair
    keyValueList.forEach((item) => {
        const placeholder = "${" + item.key + "}";
        apiItem = apiItem.split(placeholder).join(item.defaultValue);
    });
    return apiItem;
  }
}


export const API_TAB_TYPES = {
  headers:"headers",
  queryParems:"queryParameters",
  variables:"variables"
}